import { TicketIcon } from "@heroicons/react/24/solid";
import { CSVLink } from "react-csv";
import moment from "moment";

import MaterialTableTS from "../../../../components/tables/MaterialTableTS";
import DownloadButton from "../../../../components/DownloadButton";
import useGetAllAL3Data from "./hooks/useGetAllAL3";
import { useEffect, useRef, useState, useMemo } from "react";
import GroupFilters from "./AL3ReportsFilter";

interface DynamicRow {
  [key: string]: any;
  isMappedToAL3?: boolean;
}
export const AL3Report: React.FC = () => {
  const {
    data: al3ProducerCodeData,
    isLoading: al3ProducerCodeDataLoading,
    refetch,
  } = useGetAllAL3Data();

  const retriesRef = useRef(0);
  const [filteredSourceData, setFilteredSourceData] = useState<string | null>(
    null
  );

  useEffect(() => {
    const MAX_RETRIES = 3;
    if (
      (al3ProducerCodeData?.status === 503 ||
        al3ProducerCodeData?.status === 502) &&
      !al3ProducerCodeDataLoading &&
      retriesRef.current < MAX_RETRIES
    ) {
      retriesRef.current++;
      refetch();
    }
  }, [al3ProducerCodeData, refetch, al3ProducerCodeDataLoading]);

  const percentage = (partialValue: number, totalValue: number) =>
    totalValue ? (100 * partialValue) / totalValue : 0;

  const rawData: DynamicRow[] = useMemo(() => {
    if (!al3ProducerCodeData?.data) return [];
    return Array.isArray(al3ProducerCodeData.data)
      ? al3ProducerCodeData.data
      : [];
  }, [al3ProducerCodeData]);

  const filteredData: DynamicRow[] = useMemo(() => {
    return rawData
      .filter((row) =>
        filteredSourceData === null
          ? true
          : row.sourceData === filteredSourceData
      )
      .sort((a, b) => {
        const carrierA = a.carrierName ?? a.CarrierName ?? "";
        const carrierB = b.carrierName ?? b.CarrierName ?? "";
        return carrierA.localeCompare(carrierB);
      });
  }, [rawData, filteredSourceData]);

  const mappedCount = useMemo(
    () => filteredData.filter((row) => row.isMappedToAL3).length,
    [filteredData]
  );

  const exportData: DynamicRow[] = useMemo(() => {
    return filteredData.map((row) => {
      const { tableData, ...rest } = row;
      return rest;
    });
  }, [filteredData]);

  const dateTimeToday = new Date().toLocaleString();
  const csvReport = {
    filename: `AL3Report_${dateTimeToday}.csv`,
    data: exportData,
  };

  const Al3Columns = useMemo(() => {
    if (!filteredData.length) return [];
    const keys = Object.keys(filteredData[0]).filter(
      (key) => key !== "tableData"
    );

    const mappedColumns = keys.map((key) => ({
      title:
        key === "isMappedToAL3"
          ? "Is Mapped to AL3"
          : key
              .replaceAll(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase()),
      field: key,
      render: (row: DynamicRow) => {
        const value = row[key];
        if (value === null || value === undefined) return "";

        if (key === "isMappedToAL3" && typeof value === "boolean")
          return value ? "Yes" : "No";

        if (key.toLowerCase().includes("date"))
          return moment(value).isValid()
            ? moment(value).format("M/D/YYYY hh:mm A")
            : String(value);

        return value;
      },
    }));

    if (!mappedColumns.some((col) => col.title === "Source Data")) {
      mappedColumns.push({
        title: "Source Data",
        field: "SourceData",
        render: (row: DynamicRow) =>
          row.isMappedToAL3 ? row.SourceData ?? "AL3" : "Confie IQ",
      });
    }

    return mappedColumns;
  }, [filteredData]);

  return (
    <div className="text-xs items-center">
      <div className="mx-4 mt-4 overflow-x-auto">
        {/* Header */}
        <div className="flex mb-2 gap-2 items-center">
          <TicketIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
          <div className="flex flex-col leading-tight">
            <div className="text-gray-500 text-md font-medium">
              Report: AL3 Data
            </div>
            <div className="flex items-center font-bold text-lg text-primary">
              All AL3 Data Report
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-2 w-full">
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-sm">Total Mapped Records:</span>
            <span className="text-primary text-base font-semibold">
              {filteredData.length > 0
                ? percentage(mappedCount, filteredData.length).toFixed(2)
                : "0"}
              %
            </span>
            <span className="text-reportsColor text-base font-semibold">
              {mappedCount} out of {filteredData.length}
            </span>
          </div>

          <CSVLink {...csvReport}>
            <DownloadButton buttonValue="Download File" onClick={() => {}} />
          </CSVLink>
        </div>

        <hr className="border-t-2 border-pink-500 w-full my-2" />

        <GroupFilters
          searchAgreement={(source) => setFilteredSourceData(source)}
          resetFilter={() => setFilteredSourceData(null)}
        />
      </div>

      <div className="box-shadow mx-4 mb-4 mt-2 overflow-x-auto grid grid-row">
        <MaterialTableTS
          data={filteredData}
          isLoading={al3ProducerCodeDataLoading}
          columns={Al3Columns}
          showTitle={false}
          toolbarSearch={true}
          showToolbar={true}
        />
      </div>
    </div>
  );
};
